import type { AgentRollupSummary } from '@sill/types/api';

export const monoFont = "'JetBrains Mono', monospace";
export const serifFont = "'Instrument Serif', serif";
export const sansFont = "'Geist', sans-serif";

export function fmtMoney(minor: number, currency: string | null): string {
  if (minor === 0 && currency === null) return '—';
  const cur = currency ?? 'USD';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(minor / 100);
  } catch {
    return `${cur} ${(minor / 100).toFixed(2)}`;
  }
}

export function fmtInt(n: number): string {
  return n.toLocaleString('en-US');
}

export function fmtPct(num: number, den: number): string {
  if (den === 0) return '—';
  return `${Math.round((num / den) * 100)}%`;
}

export function fmtDay(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return day;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/**
 * A UA-derived name is only shown when it reads like a name a person would
 * recognise. Tokens with separators, digits-heavy blobs or long strings
 * ("3076922:aa-prod-public-product@frontend-sg3", "ms-office", "Dalvik")
 * are not names — those callers are shown by what they are.
 */
export function isHumanAgentName(name: string | null | undefined): name is string {
  if (typeof name !== 'string') return false;
  const n = name.trim();
  if (n.length < 2 || n.length > 32) return false;
  if (/[:@/\\<>{}[\]|]/.test(n)) return false;
  if ((n.match(/\d/g) ?? []).length > 3) return false;
  return /^[A-Za-z][A-Za-z0-9 .+_-]*$/.test(n);
}

const CATEGORY_GENERIC: Readonly<Record<string, string>> = {
  ai_agent: 'AI agent',
  search_crawler: 'Search crawler',
  seo_tool: 'SEO tool',
  social_preview: 'Link preview bot',
  monitor: 'Uptime monitor',
  tool: 'Script or HTTP tool',
  scanner: 'Security scanner',
  brand_monitor: 'Brand monitor',
  infrastructure: 'Infrastructure bot',
  unknown: 'Unidentified visitor',
};

export function agentDisplayName(a: Pick<AgentRollupSummary, 'inferred_name' | 'agent_id' | 'agent_key'> & { category?: string | null; visitor_class?: string | null }): string {
  // Every human browser build is folded into one row by the API.
  if (a.visitor_class === 'human_likely' || a.category === 'human') return 'Human visitors';
  const category = a.category ?? 'unknown';
  const generic = CATEGORY_GENERIC[category] ?? 'Unidentified visitor';
  const matched = a.agent_id !== null && a.agent_id !== undefined;
  // A recognisable name IS an identification — "HubSpot", not
  // "Unidentified visitor (HubSpot)". The category pill beside it says
  // what kind of caller it is; the name says who.
  if (isHumanAgentName(a.inferred_name)) return a.inferred_name;
  // A registry match implies a recognisable product; prettify the id.
  if (matched) {
    return a.agent_id!.replace(/^agent_/, '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
  // Nothing a person would recognise: say what it is, plus a short tag so
  // two of them can be told apart in a list.
  return `${generic} · ${a.agent_key.slice(0, 6)}`;
}

/**
 * The classifier's verdict (ADR-0204: one Haiku call per user-agent string,
 * shared across every merchant who sees that bot) is a RECOMMENDATION.
 * Nothing is blocked by it — only a guardrail blocks. Labels say so.
 */
export const VERDICT_LABEL: Readonly<Record<string, string>> = {
  safe: 'Looks safe',
  monitor: 'Worth watching',
  block: 'Consider blocking',
};
const VERDICT_SOURCE_HINT: Readonly<Record<string, string>> = {
  policy: 'Documented behaviour of this bot; the same call is shown to every merchant.',
  spoof: 'Decided by a verification signal, not by the name.',
  classifier: 'Sill\u2019s classifier judged this bot from its identity string and behaviour across the network; the more cautious call across its versions is shown.',
  none: '',
};
/** Tooltip for a verdict pill: the reason when the policy or a spoof signal decided, the source otherwise. Always says it is informational. */
export function verdictHint(a: { verdict_source?: string | undefined; verdict_reason?: string | null | undefined }): string {
  const src = a.verdict_source ?? 'classifier';
  const reason = a.verdict_reason ?? VERDICT_SOURCE_HINT[src] ?? '';
  return `${reason} Informational \u2014 nothing is blocked unless you add a guardrail.`.trim();
}

/** Human labels for the spoof-signal keys the edge / crawl path emits. */
export const SPOOF_SIGNAL_LABEL: Readonly<Record<string, string>> = {
  none: 'No warning',
  empty_ua: 'Sent no identity string',
  bare_ua: 'Identity string is a bare token, not a browser or documented bot',
  typo_ua: 'Identity string looks like a misspelled known bot',
  cf_unverified: 'Claims to be a known bot, but the network could not verify it',
};

export const CATEGORY_LABEL: Readonly<Record<string, string>> = {
  ai_agent: 'AI agent',
  search_crawler: 'Search crawler',
  seo_tool: 'SEO tool',
  social_preview: 'Link preview',
  monitor: 'Uptime monitor',
  brand_monitor: 'Brand monitor',
  infrastructure: 'Infrastructure',
  tool: 'Script / HTTP tool',
  scanner: 'Security scanner',
  human: 'Human browser',
  unknown: 'Unclassified',
};

/** Words for the four filters, so titles and empty states name what was asked for. */
export const KIND_WORDS: Readonly<Record<'ai' | 'bots' | 'humans' | 'all', { readonly label: string; readonly plural: string; readonly singular: string; readonly who: string }>> = {
  ai: { label: 'AI agents', plural: 'AI agents', singular: 'AI agent', who: 'Which AI agents' },
  bots: { label: 'Crawlers & tools', plural: 'crawlers & tools', singular: 'crawler or tool', who: 'Which crawlers & tools' },
  humans: { label: 'People', plural: 'human visitors', singular: 'human visitor', who: 'Which browsers' },
  all: { label: 'All', plural: 'visitors', singular: 'visitor', who: 'Who they are' },
};

/** Ranges: `1d` is today (UTC), refreshed every 15 minutes. */
export const RANGE_LABEL: Readonly<Record<'1d' | '7d' | '30d' | '90d', string>> = { '1d': 'Today', '7d': '7 days', '30d': '30 days', '90d': '90 days' };
export function rangePhrase(range: '1d' | '7d' | '30d' | '90d'): string {
  return range === '1d' ? 'today' : `in the last ${RANGE_LABEL[range]}`;
}

/** Which side of the AI-agents / crawlers-and-tools split a caller is on. */
export function agentKindOf(a: { category?: string | null; agent_id?: string | null }): 'ai' | 'bots' {
  return a.category === 'ai_agent' || ((a.category === null || a.category === undefined) && a.agent_id !== null && a.agent_id !== undefined) ? 'ai' : 'bots';
}

export const PAGE_KIND_LABEL: Readonly<Record<string, string>> = {
  product: 'Product pages',
  collection: 'Collections',
  search: 'Searches',
  cart: 'Cart',
  checkout: 'Checkout',
  other: 'Other pages',
};

/**
 * Plain-words labels for the `protocol` tallies on an agent. The raw
 * values ("embed", "worker", "plugin", "mcp", "a2a", "ap2") are how
 * Sill routes the hit server-side, but merchants don't share that
 * vocabulary. Each label describes the OBSERVATION — "we saw this
 * agent through X" — rather than naming the protocol spec.
 */
export const PROTOCOL_LABEL: Readonly<Record<string, string>> = {
  embed: 'Seen by: the Sill script on your pages',
  worker: 'Seen by: your Cloudflare Worker',
  plugin: 'Seen by: the WordPress plugin',
  shopify: 'Seen by: the Shopify app',
  woocommerce: 'Seen by: the WooCommerce plugin',
  server: 'Seen by: the Sill server',
  mcp: 'Called your MCP server',
  a2a: 'Fetched your A2A agent card',
  ap2: 'Sent an AP2 mandate',
  acp: 'Sent an ACP payment token',
  ucp: 'Read your UCP profile',
  x402: 'Requested x402 payment',
  unknown: 'Unknown route',
};

export const DECISION_LABEL: Readonly<Record<string, string>> = {
  observed: 'Observed',
  approved: 'Approved',
  rejected: 'Rejected',
  verification_rejected: 'Rejected · verification',
  rejected_post_verify: 'Rejected · post-verify',
  escalated_approved: 'Escalated · approved',
  escalated_rejected: 'Escalated · rejected',
};

/** Sort a `{key: n}` map descending by n. */
export function sortedEntries(m: Readonly<Record<string, number>>): Array<[string, number]> {
  return Object.entries(m).sort((a, b) => b[1] - a[1]);
}

export function sumValues(m: Readonly<Record<string, number>>): number {
  return Object.values(m).reduce((s, v) => s + v, 0);
}
